import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, CircularProgress, Alert, Chip, Accordion, AccordionSummary, AccordionDetails, Pagination } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { fetchHistory } from '../../../store/strategistSlice';
import { parseACText } from '../strategistAgentUtils';
import { COLORS } from '../../../shared/colors';
import { AC_FORMATS } from '../../../shared/appConstants';

const ACHistoryTab = ({ selectedProjects }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [lastFetchParams, setLastFetchParams] = useState(null);
  const limit = 10;
  const { history, loading, error } = useSelector((state) => state.strategist);
  const { data: historyData = [], totalPages = 0 } = history || {};

  useEffect(() => {
    const params = {
      type: 'ac',
      page,
      limit
    };
    
    if (selectedProjects && selectedProjects.length > 0) {
      params.projects = selectedProjects;
    }
    
    const paramsString = JSON.stringify(params);
    if (paramsString !== lastFetchParams) {
      setLastFetchParams(paramsString);
      dispatch(fetchHistory(params));
    }
  }, [selectedProjects, dispatch, page, limit, lastFetchParams]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  if (loading.fetchHistory) {
    return (
      <Box className="history-loading">
        <CircularProgress />
        <Typography variant="body2" className="history-loading-text">
          {t('agent.history.loading')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" className="history-error">
        {error}
      </Alert>
    );
  }

  if (!historyData || historyData.length === 0) {
    return (
      <Box className="history-empty">
        <Typography variant="h6" className="history-empty-title">
          {t('agent.history.empty.title')}
        </Typography>
        <Typography variant="body2" className="history-empty-description">
          {t('agent.history.empty.description')}
        </Typography>
      </Box>
    );
  }

  // Data is already flattened - each item is a history entry with project info
  const flattenedData = historyData.map(item => {
    const criteria = item.agent_output?.text ? parseACText(item.agent_output.text, item.format) : null;
    return {
      ...item,
      criteria
    };
  });

  // Group flattened items by project name
  const groupedByProject = flattenedData.reduce((acc, item) => {
    const projectName = item.project_name || 'Unknown Project';
    if (!acc[projectName]) {
      acc[projectName] = [];
    }
    acc[projectName].push(item);
    return acc;
  }, {});

  return (
    <Box className="ac-history-tab">
      {Object.entries(groupedByProject).map(([projectName, items]) => (
        <Accordion 
          key={projectName}
          defaultExpanded
          sx={{ 
            mb: 2, 
            border: `1px solid ${COLORS.gray[200]}`,
            '&:before': { display: 'none' },
            borderRadius: '10px !important',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08) !important',
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{ 
              bgcolor: COLORS.blue[50],
              borderBottom: `1px solid ${COLORS.gray[200]}`,
              '&:hover': { bgcolor: COLORS.blue[100] },
              '& .MuiAccordionSummary-content': {
                alignItems: 'center'
              }
            }}
          >
            <Typography variant="h6" fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {projectName}
              <Box
                sx={{
                  background: COLORS.primary,
                  color: 'white',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  fontSize: '0.7rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1
                }}
              >
                {items.length}
              </Box>
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ 
            p: 2, 
            bgcolor: COLORS.gray[25] || '#fafafa'
          }}>
            <Box className="history-items-list">
              {items.map((item) => {
                // Check if this is a failed item
                const isFailed = item.status === 'FAILED' || (item.agent_output && (item.agent_output.error || item.agent_output.parseError));
                const errorMessage = item.agent_output?.error || item.agent_output?.parseError || t('agent.history.agentProcessingError');
                
                if (isFailed) {
                  return (
                    <Paper key={`${item.project_id}-${item.ticket_number}`} elevation={0} className="ac-history-item">
                      <Box className="ac-history-item-header">
                        <Box className="ac-history-item-title-section">
                          <Typography variant="body2" className="ac-history-item-ticket-number">
                            {item.ticket_number}
                          </Typography>
                          <Typography variant="body2" className="ac-history-item-title">
                            {item.ticket_title}
                          </Typography>
                          <Chip label={item.format} size="small" className="ac-history-item-format-chip" />
                        </Box>
                        {item.created_at && (
                          <Typography variant="caption" className="ac-history-item-date">
                            {item.created_at}
                          </Typography>
                        )}
                      </Box>
                      <Box className="ac-history-item-content">
                        <Alert severity="error">
                          {errorMessage}
                        </Alert>
                      </Box>
                    </Paper>
                  );
                }
                
                return (
                  <Paper key={`${item.project_id}-${item.ticket_number}`} elevation={0} className="ac-history-item">
          <Box className="ac-history-item-header">
            <Box className="ac-history-item-title-section">
              <Typography variant="body2" className="ac-history-item-ticket-number">
                {item.ticket_number}
              </Typography>
              <Typography variant="body2" className="ac-history-item-title">
                {item.ticket_title}
              </Typography>
              <Chip label={item.format} size="small" className="ac-history-item-format-chip" />
            </Box>
            {item.created_at && (
              <Typography variant="caption" className="ac-history-item-date">
                {item.created_at}
              </Typography>
            )}
          </Box>

          <Box className="ac-history-item-content">
            {!item.agent_output || !item.agent_output.text || item.agent_output.error || item.agent_output.parseError || !item.criteria || item.criteria.length === 0 ? (
              <Box className="ac-history-no-content">
                <Typography variant="body2" className="ac-history-no-content-text">
                  {t('agent.history.unableToFetchData')}
                </Typography>
              </Box>
            ) : item.format === AC_FORMATS.GIVEN_WHEN_THEN || item.format === 'given-when-then' ? (
              <Box className="ac-history-gwt-list">
                {item.criteria?.map((criterion, idx) => (
                  <Box key={idx} className="ac-history-gwt-item">
                    <Box className="ac-history-gwt-section">
                      <Chip label={t('agent.ac.given')} size="small" className="ac-history-chip-given" />
                      <Typography variant="body2" className="ac-history-gwt-text">
                        {criterion.given}
                      </Typography>
                    </Box>
                    <Box className="ac-history-gwt-section">
                      <Chip label={t('agent.ac.when')} size="small" className="ac-history-chip-when" />
                      <Typography variant="body2" className="ac-history-gwt-text">
                        {criterion.when}
                      </Typography>
                    </Box>
                    <Box className="ac-history-gwt-section">
                      <Chip label={t('agent.ac.then')} size="small" className="ac-history-chip-then" />
                      <Typography variant="body2" className="ac-history-gwt-text">
                        {criterion.then}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Paper elevation={0} className={`ac-list-card ${item.format === AC_FORMATS.CHECKLIST || item.format === 'checklist' ? 'ac-checklist-card' : ''}`}>
                {item.criteria?.map((criterion, idx) => {
                  const displayText = typeof criterion === 'string' ? criterion : String(criterion);

                  if (item.format === AC_FORMATS.CHECKLIST || item.format === 'checklist') {
                    return (
                      <Box key={idx} className="ac-checklist-item">
                        <Box className="ac-checklist-indicator" />
                        <Typography variant="body1" className="ac-checklist-text">
                          {displayText}
                        </Typography>
                      </Box>
                    );
                  }

                  return (
                    <Box key={idx} className="ac-list-item">
                      <Typography variant="body1" className="ac-list-text">
                        {displayText}
                      </Typography>
                    </Box>
                  );
                })}
              </Paper>
            )}
          </Box>
        </Paper>
              );
              })}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
      
      <Box className="history-pagination-container">
        <Pagination
          count={totalPages || 1}
          page={page}
          onChange={handlePageChange}
          color="primary"
          showFirstButton
          showLastButton
          disabled={!totalPages || totalPages <= 1}
          slotProps={{
            previous: { title: t('pagination.previous') },
            next: { title: t('pagination.next') },
            first: { title: t('pagination.first') },
            last: { title: t('pagination.last') }
          }}
        />
      </Box>
    </Box>
  );
};

export default ACHistoryTab;

